import { AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '../Button/Button'
import type { WalkthroughPhase } from './useWalkthroughSession'

const STARTING_TITLE: Record<string, string> = {
  start: 'Starting walkthrough…',
  resume: 'Resuming walkthrough…',
  rebuild: 'The strategy code changed — building a new walkthrough…',
}

export interface WalkthroughStartOverlayProps {
  phase: WalkthroughPhase
  /** True once the run has been going long enough to deserve an escape hatch. */
  slow: boolean
  onBackToCanvas: () => void
  onRetry: () => void
  onManageBilling?: () => void
  className?: string
}

/**
 * Covers the chart while the walkthrough's backtest is being prepared and run, and carries the
 * error state if it fails.
 *
 * The user chose not to cap the drawn span, so a wide walkthrough can legitimately take
 * minutes. That makes a real progress bar (fed from the engine's own `progress_backtest`) the
 * difference between "working" and "stuck" — a bare spinner would be dishonest here.
 */
export function WalkthroughStartOverlay({
  phase,
  slow,
  onBackToCanvas,
  onRetry,
  onManageBilling,
  className,
}: WalkthroughStartOverlayProps) {
  if (phase.kind !== 'starting' && phase.kind !== 'error') return null

  return (
    <div
      className={cn(
        'absolute inset-0 z-[120] flex items-center justify-center bg-background/70 backdrop-blur-[2px]',
        className,
      )}
    >
      {phase.kind === 'starting' ? (
        <div
          role="status"
          aria-live="polite"
          className="w-[19rem] rounded-lg border border-border bg-popover shadow-modal p-4"
        >
          <div className="flex items-center gap-2.5">
            <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse" />
            <span className="text-section text-foreground">
              {STARTING_TITLE[phase.reason] ?? STARTING_TITLE.start}
            </span>
          </div>

          <p className="mt-2 text-caption text-muted-foreground leading-relaxed">
            {phase.runStatus === null
              ? 'Working out the settings for the period you drew on.'
              : phase.runStatus === 0
                ? 'Queued — waiting for a run slot.'
                : 'Replaying your strategy over the period you drew on.'}
          </p>

          {/* Only meaningful once the engine has started reporting; before that a bar at 0%
              would read as stalled. */}
          {phase.runStatus === 1 && (
            <div className="mt-3">
              <div className="h-1 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-[width] duration-500"
                  style={{ width: `${phase.progressPct}%` }}
                />
              </div>
              <div className="mt-1 text-eyebrow tabular-nums text-muted-foreground text-right">
                {phase.progressPct}%
              </div>
            </div>
          )}

          {slow && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-caption text-muted-foreground">
                Still going — a wide drawing span takes longer.
              </span>
              <Button variant="ghost" size="sm" onClick={onBackToCanvas} className="shrink-0">
                Back to Canvas
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div
          role="alert"
          className="w-[19rem] rounded-lg border border-border bg-popover shadow-modal p-4"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-danger-dim text-danger">
            <AlertTriangle size={16} strokeWidth={1.75} />
          </span>
          <div className="mt-3 text-section text-foreground">Walkthrough didn&apos;t start</div>
          <p className="mt-1 text-body text-muted-foreground leading-relaxed">{phase.message}</p>
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onBackToCanvas}>
              Back to Canvas
            </Button>
            {phase.action === 'billing' && onManageBilling ? (
              <Button size="sm" onClick={onManageBilling}>
                Manage billing
              </Button>
            ) : (
              <Button size="sm" onClick={onRetry}>
                Try again
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
